<template>
  <div class="modal is-active" v-if="isActive">
    <div class="modal-background" qa="whiteboard_modal"></div>
    <div class="modal-content">
      <div ref="toolbarContainerTop" class="toolbar-container-top">
        <div @click="save" class="button done-btn is-rounded" ><span qa="whtbrd-done-button">{{ $t('done') }}</span></div>
        <top-toolbar class="top-toolbar" :callback="handleToolbar"></top-toolbar>
      </div>
      <div ref="container" class="wrapper">
      <v-touch :class="{'is-hidden': !editor || error}" class="canvas-wrapper" @panstart="touchStart" @panmove="touchMove" @panend="touchEnd" @pinchstart="touchStart" @pinchmove="touchMove" @pinchend="touchEnd">
        <div class="canvas" ref="canvas" @click="closeToolbarMenu()" @wheel="wheelMove" @dragover="showDrop">
          <div class="saving" v-if="saving">
            Saving...
          </div>
          <div class="saving" v-if="isAttaching">
            Attaching...
            <progress class="progress is-primary is-large" :value="uploadStatus.progress" max="100"></progress>
          </div>
          <div class="reload" v-if="loadingTimeout">
            <article class="message is-primary">
              <div class="message-body">
                Board not loading? <a @click="initCanvas">{{ $t('retry') }}</a>
              </div>
            </article>
          </div>
          <canvas id="c" class="canvasBorder" :class="{hideCanvas: !loaded}"></canvas>
          <context-menu :config="contextMenuSettings" :callback="handleContextMenu" :styleSettings="styleSettings"></context-menu>
        </div>
      </v-touch>
      </div>
      <div ref="toolbarContainerBottom" class="toolbar-container-bottom is-flex">
        <toolbar ref="toolbar" :callback="handleToolbar" :canvasSize="canvasSize" :preview="!this.editor" :edit="toggleEdit"></toolbar>
        <zoom-controls :callback="controlZoom"></zoom-controls>
      </div>
      <loading-error class="editor" v-if="error" :message="error" :retry="retryInit"></loading-error>
      <palettes v-if="styleSettings.active" :settings="styleSettings" :update="applyStyle" :close="postDialog"></palettes>

      <modal-attach :parentID="_uid" @cancel="cancelImageAttachment" @attached="handleImageAttachment"></modal-attach>

      <confirm ref="editBoardConfirm" title="Edit Whiteboard" :text="editBoardConfirmText" @yes="confirmToggleEdit"></confirm>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import VueTouch from 'vue-touch'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'

import board from './src/board'

import TopToolbar from './TopToolbar'
import Toolbar from './Toolbar'

import ZoomControls from './ZoomControls'
import ContextMenu from './ContextMenu'
import Palettes from './palettes'
import LoadingError from './LoadingError'

import Confirm from '@/components/Modals/Confirm'
import ModalAttach from '@/components/Modals/Attach'

import i18n from '@/i18n'

Vue.use(VueTouch, { name: 'v-touch' })

const zoomSettings = {
  default: 1,
  min: 0.1,
  max: 5,
  step: 0.05
}

const numLoadingChecks = 5

const errorMessage = 'Unable to load editor'

const viewNone = 0
const viewPreview = 1
const viewEditor = 2

let lastTouchPosition = {}

export default {
  components: {
    History,
    Toolbar,
    TopToolbar,
    ZoomControls,
    ContextMenu,
    Palettes,
    LoadingError,
    Confirm,
    ModalAttach
  },
  mixins: [auth, componentState],
  data: function () {
    return {
      newBoard: false,
      backgroundImage: null,
      boardView: viewNone,
      loaded: false,
      saving: false,
      canvasSize: { width: 100, height: 100 },
      objectStatus: { loaded: 0, added: 0, expected: board.expectedNotLoaded },
      uploadStatus: {},
      styleSettings: {
        active: false,
        mode: '',
        supportsFill: true,
        supportsBorder: true,
        supportsText: true,
        border: board.stroke,
        fill: board.fill,
        text: board.text
      },
      contextMenuSettings: {
        active: false,
        fill: false,
        border: false,
        text: false
      },
      showHelp: false,
      snap: false,
      zoomLevel: zoomSettings.default,
      disableShortcuts: false,
      mode: '',
      dialogName: '',
      error: '',
      loadingTimeout: false,
      loadingProgress: 0,
      initializing: false,
      sorryTitle: i18n.t('magicboard.sorryTitle'),
      sorryText: i18n.t('magicboard.sorryText'),
      seenIEModal: false,
      active: false,
      keys: { storage: '', board: '' },
      annotate: false
    }
  },
  computed: {
    colour: function () {
      return this.colours.hex
    },
    preview: function () {
      return this.boardView === viewPreview
    },
    editor: function () {
      return this.boardView === viewEditor
    },
    editBoardConfirmText: function () {
      return `You're currently logged in as ${this.currentUser.userName}. Are you sure you want to edit the whiteboard as that user?`
    },
    isActive: function () {
      return this.active
    },
    isAttaching: function () {
      return this.uploadStatus.running && !this.uploadStatus.visible && !this.uploadStatus.error
    }
  },
  watch: {
    project: function () {
      if (this.editor) {
        this.renderCanvas()
      } else {
        this.boardView = viewPreview
      }
    }
  },
  methods: {
    startListeners: function () {
      window.addEventListener('resize', this.updateCanvasSize)
      window.addEventListener('keydown', this.handleKeyboardShortcut)
      window.addEventListener('paste', this.handlePaste)
    },
    stopListeners: function () {
      window.removeEventListener('resize', this.updateCanvasSize)
      window.removeEventListener('keydown', this.handleKeyboardShortcut)
      window.removeEventListener('paste', this.handlePaste)
      if (board) {
        board.stopListeners()
      }
    },
    renderCanvas: function () {
      if (this.loaded) {
        this.redrawCanvas()
      } else {
        this.initCanvas()
      }
    },

    boardConfig: function (token) {
      return {
        id: 'c',
        boardKey: this.keys.board,
        snap: this.snap,
        pixelRatio: window.devicePixelRatio,
        styleSettings: this.styleSettings,
        objectStatus: this.objectStatus,
        clickMenu: this.clickMenuItem,
        configureToolbar: this.configureContextMenu,
        new: this.newBoard
      }
    },

    initCanvas: function () {
      if (this.initializing || !this.active) {
        return
      }

      this.initializing = true
      this.loaded = false
      this.objectStatus = { loaded: 0, added: 0, expected: board.expectedNotLoaded }
      this.canvasSize = { width: 100, height: 100 }
      this.updateCanvasSize()

      this.watchLoadingProgress()

      board.init(this.boardConfig()).then(() => {
        board.setSize(this.canvasSize.width, this.canvasSize.height)
        board.afterLoaded(() => {
          this.zoomLevel = board.zoomToFit(zoomSettings.min, zoomSettings.step)
          this.loaded = true
          this.loadingTimeout = false

          if (this.backgroundImage) {
            board.setBackgroundImage(this.backgroundImage).then((size) => {
              this.updateCanvasSize(size.width, size.height)
            })
          } else if (board.hasBackgroundImage()) {
            const size = board.getBackgroundImageSize()
            this.updateCanvasSize(size.width, size.height)
            board.origin()
          } else {
            board.centre()
          }
        })
        this.clickMenuItem('Select')
        this.initializing = false
      }, () => {
        this.error = errorMessage
        this.initializing = false
      })
    },

    redrawCanvas: function () {
      this.updateCanvasSize()
      board.setSize(this.canvasSize.width, this.canvasSize.height)
      board.redraw()
    },

    updateCanvasSize: function (width, height) {
      const canvas = this.$refs.canvas
      if (!canvas) {
        return
      }

      const boundingRect = this.$refs.container.getBoundingClientRect()

      width = boundingRect.width
      height = boundingRect.height
      let zoom = null

      // for background images, maintain proportions scaled to bounding box
      if (board.hasBackgroundImage()) {
        const size = board.getBackgroundImageSize()
        width = size.width
        height = size.height

        const rw = boundingRect.width / width
        const rh = boundingRect.height / height

        if (height * rw < boundingRect.height) {
          width = boundingRect.width
          height = height * rw
          zoom = rw
        } else if (width * rh < boundingRect.width) {
          width = width * rh
          height = boundingRect.height
          zoom = rh
        }
      }

      this.canvasSize.width = width
      this.canvasSize.height = height

      canvas.style.height = `${this.canvasSize.height}px`
      canvas.style.maxHeight = canvas.style.height
      canvas.style.minHeight = canvas.style.height

      canvas.style.width = `${this.canvasSize.width}px`
      canvas.style.maxWidth = canvas.style.width
      canvas.style.minWidth = canvas.style.width

      if (board) {
        board.setSize(this.canvasSize.width, this.canvasSize.height)
        if (zoom) {
          board.scaleBackgroundImage(zoom)
        }
      }
    },

    setZoom: function (zoomIn = true, event) {
      if (zoomIn) {
        this.zoomLevel += zoomSettings.step
        if (this.zoomLevel > zoomSettings.max) {
          this.zoomLevel = zoomSettings.max
        }
      } else {
        this.zoomLevel -= zoomSettings.step
        if (this.zoomLevel < zoomSettings.min) {
          this.zoomLevel = zoomSettings.min
        }
      }

      this.zoomLevel = Math.round(this.zoomLevel * 100) / 100
      board.setZoom(this.zoomLevel, event)
    },

    getTouchDistance: function (event) {
      if (event.pointers.length < 2) {
        return 0
      }

      return Math.sqrt(
        Math.pow(event.pointers[1].clientX - event.pointers[0].clientX, 2) +
        Math.pow(event.pointers[1].clientY - event.pointers[0].clientY, 2)
      )
    },

    setTouchPosition: function (event) {
      const sampleSize = 5

      lastTouchPosition.x = event.center.x
      lastTouchPosition.y = event.center.y
      lastTouchPosition.count++

      lastTouchPosition.distances.push(this.getTouchDistance(event))

      let current = lastTouchPosition.distances[0]
      if (lastTouchPosition.distances.length > sampleSize) {
        current = lastTouchPosition.distances.shift()
      }

      let sum = 0
      lastTouchPosition.distances.forEach((d) => {
        sum += d - current
        current = d
      })

      lastTouchPosition.averageDistance = sum / lastTouchPosition.distances.length
    },

    isValidTouchEvent: function (event) {
      return event.pointers.length === 2 || this.mode === 'Pan'
    },

    touchStart: function (event) {
      if (!this.isValidTouchEvent(event)) {
        return
      }

      // disable drawing
      board.setMode('pan')

      lastTouchPosition = {
        x: 0,
        y: 0,
        count: 0,
        distances: [],
        averageDistance: 0
      }

      this.setTouchPosition(event)
    },

    touchEnd: function (event) {
      if (!this.isValidTouchEvent(event)) {
        return
      }

      // re-enable drawing
      board.setMode(this.mode)
    },

    touchMove: function (event) {
      if (!this.isValidTouchEvent(event)) {
        return
      }

      const zoomDistance = 2 // diff change to perform zoom
      const zoomFrequency = 3 // # of touch events per zoom

      const point = {
        x: event.center.x - lastTouchPosition.x,
        y: event.center.y - lastTouchPosition.y
      }

      board.setPan(point)

      this.setTouchPosition(event)

      if (event.pointers.length === 1) {
        return
      }

      if (lastTouchPosition.count % zoomFrequency === 0) {
        if (lastTouchPosition.averageDistance > zoomDistance) {
          this.setZoom(true, event)
        } else if (lastTouchPosition.averageDistance < -zoomDistance) {
          this.setZoom(false, event)
        }
      }
    },

    wheelMove: function (event) {
      const point = { x: 0, y: 0 }

      if (Math.abs(event.wheelDeltaY) >= 100) {
        // zooming

        if (event.deltaY < 0) {
          this.setZoom(true, event) // zoom in
        } else if (event.deltaY > 0) {
          this.setZoom(false, event) // zoom out
        }
      } else {
        // panning
        const maxPan = 25

        // mouse left / right
        if (Math.abs(event.deltaX) >= 1) {
          const delta = event.deltaX < 0 ? Math.max(event.deltaX, -maxPan) : Math.min(event.deltaX, maxPan)
          point.x = -1 * delta
        }

        // mouse up / down
        if (Math.abs(event.deltaY) >= 1) {
          const delta = event.deltaY < 0 ? Math.max(event.deltaY, -maxPan) : Math.min(event.deltaY, maxPan)
          point.y = -1 * delta
        }

        board.setPan(point)
      }

      event.preventDefault()
    },

    attach: function (payload, point, close, errorHandler) {
      if (payload.file) {
        board.uploadFile(payload.file, this.uploadStatus, point, close, errorHandler)
      } else if (payload.url) {
        board.addFileFromURL(payload.url, this.uploadStatus, point, close, errorHandler)
      }
    },

    setMode: function (mode) {
      mode = mode === 'Select' ? '' : mode
      this.mode = mode
      if (this.editor) {
        board.setMode(mode)
      }
    },

    resetUploadStatus: function (name) {
      this.uploadStatus = {
        name: name,
        progress: 0,
        done: false,
        error: false,
        paused: false,
        running: false,
        selection: board.hasSelection(),
        drop: null,
        autoClose: false,
        visible: false
      }
    },

    applyStyle: function () {
      board.applyStyleSettings()
    },
    handlePaste: function (event) {
      if (this.disableShortcuts) {
        return
      }

      if (navigator.clipboard && (event.clipboardData || event.originalEvent.clipboardData).items) {
        board.pasteImageToFile(event).then(file => {
          this.resetUploadStatus('image')
          board.uploadFile(file, this.uploadStatus)
          this.postDialog(true)
        }).catch(() => {
          return board.paste(event)
        })
      } else {
        return board.paste(event)
      }
    },
    handleKeyboardShortcut: function (event) {
      if (this.disableShortcuts) {
        return
      }

      // copy - ctrl+c or meta+c
      if (event.keyCode === 67 && (event.ctrlKey || event.metaKey)) {
        return board.copy()
      }
      // cut - ctrl+x or meta+x
      if (event.keyCode === 88 && (event.ctrlKey || event.metaKey)) {
        return board.cut()
      }

      // zoom-in ctrl+= or meta+=
      if ((event.keyCode === 187 || event.keyCode === 61) && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        return this.setZoom(true, event)
      }
      // zoom-out ctrl+- or meta+-
      if ((event.keyCode === 189 || event.keyCode === 173) && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        return this.setZoom(false, event)
      }
      // undo / redo - ctrl+z or meta+z
      if (event.keyCode === 90 && (event.ctrlKey || event.metaKey)) {
        if (event.shiftKey) {
          return board.redo()
        }
        return board.undo()
      }
      // redo - ctrl+y or meta+y
      if (event.keyCode === 89 && (event.ctrlKey || event.metaKey)) {
        return board.redo()
      }
      // delete - backspace or delete
      if (event.keyCode === 8 || event.keyCode === 46) {
        board.delete()
      }
    },
    setDisableShortcuts: function (disable) {
      this.disableShortcuts = disable
    },
    preDialog: function (name) {
      this.disableShortcuts = true
      this.dialogName = name
    },

    postDialog: function (success) {
      this.disableShortcuts = false
      if (this.dialogName !== 'Colours') {
        if (!success) {
          this.$refs.toolbar.revert()
        } else {
          this.clickMenuItem('Select')
        }
      }
    },

    closeToolbarMenu: function () {
      this.$refs.toolbar.cancel()
    },

    clickMenuItem: function (item) {
      if (this.$refs.toolbar) {
        this.$refs.toolbar.select(item)
      }
      this.handleToolbar(item)
    },

    handleToolbar: function (name) {
      this.configureContextMenu(name)
      switch (name) {
        case 'Pan':
        case 'Select':
        case 'Line':
        case 'Arrow':
        case 'Rectangle':
        case 'Ellipse':
        case 'Triangle':
        case 'Pencil':
        case 'Text':
        case 'Erase':
          return this.setMode(name)
        case 'Sticky Note':
          return board.addStickyNote()
        case 'Bring to Front':
          board.bringToFront()
          break
        case 'Send to Back':
          board.sendToBack()
          break
        case 'Copy':
        case 'copy':
          return board.copy()
        case 'Paste':
        case 'paste':
          return board.paste()
        case 'Cut':
          return board.cut()
        case 'Delete':
          return board.delete()
        case 'Undo':
        case 'undo':
          return board.undo()
        case 'Redo':
        case 'redo':
          return board.redo()
        case 'Image':
          this.preDialog()
          return this.$store.dispatch('showAttachModal', { type: 'image', parentID: this._uid })
      }
    },
    configureContextMenu: function (nameOrType, useSettings) {
      let active = false
      let fill = false
      let border = false
      let text = false

      if (useSettings) {
        if (this.styleSettings.supportsFill) {
          fill = true
        }

        if (this.styleSettings.supportsBorder) {
          if (!this.styleSettings.supportsFill) {
            fill = true
          } else {
            border = true
          }
        }

        if (this.styleSettings.supportsText) {
          text = true
        }

        active = fill || border || text
      } else {
        nameOrType = nameOrType.toLowerCase()

        switch (nameOrType) {
          case 'line':
          case 'arrow':
          case 'pencil':
          case 'path':
            active = fill = true
            this.styleSettings.supportsFill = false
            this.styleSettings.supportsBorder = true
            this.styleSettings.supportsText = false
            break
          case 'rect':
          case 'rectangle':
          case 'ellipse':
          case 'triangle':
            active = fill = border = true
            this.styleSettings.supportsFill = true
            this.styleSettings.supportsBorder = true
            this.styleSettings.supportsText = false
            break
          case 'text':
          case 'i-text':
            active = text = true
            this.styleSettings.supportsFill = false
            this.styleSettings.supportsBorder = false
            this.styleSettings.supportsText = true
            break
          case 'sticky note':
          case 'note':
            active = fill = text = true
            this.styleSettings.supportsFill = true
            this.styleSettings.supportsBorder = false
            this.styleSettings.supportsText = true
            break
          case 'shapeWithText':
            active = fill = border = text = true
            this.styleSettings.supportsFill = true
            this.styleSettings.supportsBorder = true
            this.styleSettings.supportsText = true
            break
        }
      }

      this.contextMenuSettings = { active, fill, border, text }
    },
    handleContextMenu: function (name) {
      if (name === 'bringToFront') {
        return board.bringToFront()
      } else if (name === 'sendToBack') {
        return board.sendToBack()
      }

      this.preDialog('Colours')
      board.updateStyleSettings(this.styleSettings)
      this.styleSettings.active = true
      this.styleSettings.mode = name
    },
    showDrop: function (event) {
      this.clickMenuItem('Attach')
      this.uploadStatus.autoClose = true
    },
    toggleEdit: function () {
      this.confirmToggleEdit()
    },
    confirmToggleEdit: function () {
      this.boardView = viewEditor

      if (this.editor) {
        this.$emit('editWB')
        this.$nextTick(() => { this.renderCanvas() })
      } else {
        this.$nextTick(() => { this.updateCanvasSize() })
      }
    },
    controlZoom: function (type, event) {
      if (type.reset) {
        this.zoomLevel = board.zoomToFit(zoomSettings.min, zoomSettings.step)
        if (board.hasBackgroundImage()) {
          board.origin()
        } else {
          board.centre()
        }
      } else {
        this.setZoom(!!type.in, {}) // pass an empty event to force zoom from centre
      }
    },
    retryInit: function () {
      this.error = ''
      this.saving = false
      this.initializing = false
      this.$nextTick(this.initCanvas)
    },
    findObject: function (key, type) {
      return board.findObjectByKeyAndType(key, type)
    },
    editBoard: function (key, image, uuid, annotate) {
      this.show()

      this.newBoard = !key
      this.backgroundImage = image || null
      this.loaded = false
      this.annotate = !!annotate
      this.updateKeys(key || '', uuid)
      this.boardView = viewEditor
      this.$nextTick(() => { this.renderCanvas() })
    },
    watchLoadingProgress: function (count) {
      if (!count) {
        this.loadingTimeout = false
        this.loadingProgress = 0
      }

      count = count || 1

      if (this.loaded) {
        this.loadingTimeout = false
        return
      }

      if (count >= numLoadingChecks) {
        this.loadingTimeout = true
        return
      }

      if (this.loadingProgress === this.objectStatus.added) {
        count++
      }
      this.loadingProgress = this.objectStatus.added

      setTimeout(() => { this.watchLoadingProgress(count) }, 1000)
    },
    reset: function () {
      this.boardView = viewNone
      this.$nextTick(() => { this.updateCanvasSize() })
    },
    toggleIEModal: function () {
      this.seenIEModal = !this.seenIEModal
    },
    clear: function () {
      if (!this.error) {
        if (this.newBoard) {
          board.clear()
        } else {
          board.restore()
        }
      }

      this.$emit('close')
      this.active = false
      this.stopListeners()
    },
    show: function (type) {
      this.active = true
      this.startListeners()
    },
    save: function () {
      if (this.error) {
        this.$emit('save', null)
        this.active = false
        this.stopListeners()
        return
      }
      const status = {}
      board.finalize()
      if (board.numberOfObjects() === 0 && !board.hasBackgroundImage()) {
        board.clear()
        this.$emit('save', null)
        this.active = false
        this.stopListeners()
      } else {
        if (!board.isDirty() && !this.annotate) {
          this.$emit('close')
          this.active = false
          this.stopListeners()
          return
        }

        this.saving = true

        // use a slight delay so the save modal appears
        // next tick doesn't seem to work in this case
        setTimeout(() => {
          board.generatePreview(status).then((url) => {
            this.saving = false
            this.$emit('save', { url, key: this.keys.storage })
            this.active = false
            this.stopListeners()
          }, () => {
            this.savng = false
          })
        }, 100)
      }
    },
    updateKeys: function (key, uuid) {
      uuid = uuid || this.currentUser.uuid
      this.keys.storage = key || new Date().getTime().toString(16).split('').reverse().join('')
      this.keys.board = `${uuid}/${this.keys.storage}`
    },
    handleImageAttachment: function (attachment) {
      this.resetUploadStatus('image')
      this.attach(attachment)
      this.postDialog(true)
    },
    cancelImageAttachment: function (attachment) {
      this.postDialog(false)
    }
  }
}

</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  .canvas {
    position: relative;
  }

  .hideCanvas {
    opacity: 0;
  }

  .loading, .dropTarget, .saving {
    position: absolute;
    z-index: 15;
    height: 100vh;
    font-size: 5rem;
    padding: 35vh 5rem;
    left: 0;
    right: 0;
    @include mobile {
      font-size: 2rem;
      position: absolute;
      width: 100%;
      left: 0;
    }
  }

  .saving {
    text-align: center;
    background: black;
    opacity: 0.5;
  }

  .reload {
    position: absolute;
    z-index: 16;
    height: 100vh;
    padding: 42vh 10rem;
    left: 0;
    right: 0;
    @include mobile {
      position: absolute;
      width: 100%;
      left: 0;
      padding: 42vh 1rem;
    }
    text-align: center;
    font-size: 2rem;
    a {
      text-decoration: underline;
    }
  }

  .toolbar-container-top {
    height: 3rem;
    position: relative;
    display: flex;
  }

  .toolbar-container-bottom{
    justify-content: space-between;
    // position: absolute;
    // bottom: 0;
    // width: 100%;
    height: auto;
    margin: 0 10vh;
    @include touch {
      margin: 0;
    }
  }

  .modal-content {
    width: 100%;
    height: 100%;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 100%;
  }

  .wrapper {
    margin: 1rem 2rem;
    @include touch {
      margin: 1rem 0;
    }
  }

  .canvas-wrapper {
    justify-content: space-around;
    display: flex;
    min-height: calc(100vh - 9rem);
    max-height: calc(100vh - 9rem);
    @include touch {
      min-height: calc(100vh - 15rem);
      max-height: calc(100vh - 15rem);
    }
    .canvas {
      margin: auto;
    }
  }

  .done-btn {
    position: absolute;
    right: 2rem;
    top: 0.5rem;
    @include touch {
      right: 1rem;
      top: 0.5rem;
    }
    span {
      padding: 0.25rem 1rem;
    }
  }
</style>
<style lang="scss">
@import '~styles/utils.scss';
 .canvas-container {
    margin: auto;
  }

  /* fix text input on iOS */
  textarea[autocapitalize=off] {
    font-size: 16pt !important;
  }

  .board-preview {
    background-color: white;
    height: 77vh;
    .preview {
        height: 100%;
        max-height: 100%;
      svg {
        height: 100%;
        max-height: 100%;
      }
    }

    &.view-only {
      border: none;
    }
  }
</style>
